import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, DatePicker, Descriptions, Form, Input,
  InputNumber, Row, Select, Table, Tag, Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import apiClient from '@/shared/api/client'
import {
  spareProcurementApi,
  type POAdvance,
} from '@/features/maintenance/spareProcurementApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography

interface PORow {
  id: string
  po_no: string
  status: string
  supplier_id: string | null
  supplier_name?: string
  total_value?: number
}

/**
 * Phase 3.2-B — Record an advance against a PO.
 * Spec §7. Also shows existing advances for the same PO with remaining
 * balance — read-only, backend-calculated.
 */
export default function POAdvancePaymentPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [form] = Form.useForm()
  const [poId, setPoId] = useState<string | undefined>()

  const { data: pos } = useQuery<PORow[]>({
    queryKey: ['pos-for-advance', plantId],
    enabled: !!plantId,
    queryFn: () => apiClient.get('/purchase-orders', {
      params: { plant_id: plantId },
    }).then((r) => r.data),
  })
  const po = (pos ?? []).find((p) => p.id === poId)

  const { data: advances, refetch: refetchAdvances } = useQuery<POAdvance[]>({
    queryKey: ['po-advances', poId],
    enabled: !!poId,
    queryFn: () => spareProcurementApi.advancesForPO(poId!),
  })

  const totalAdvanceRemaining = (advances ?? []).reduce(
    (a, r) => a + (r.remaining ?? 0), 0,
  )
  const totalAdvancePaid = (advances ?? []).reduce(
    (a, r) => a + (r.amount ?? 0), 0,
  )

  const submit = useMutation({
    mutationFn: (v: {
      po_id: string
      amount: number
      advance_date: dayjs.Dayjs
      expected_settlement_date?: dayjs.Dayjs
      purpose?: string
      bank_mode?: string
      reference_no?: string
    }) => spareProcurementApi.recordAdvance({
      po_id: v.po_id,
      amount: Number(v.amount),
      advance_date: v.advance_date.format('YYYY-MM-DD'),
      expected_settlement_date: v.expected_settlement_date?.format('YYYY-MM-DD'),
      purpose: v.purpose,
    }),
    onSuccess: () => {
      message.success('Advance recorded')
      form.resetFields(['amount', 'expected_settlement_date', 'purpose',
                        'bank_mode', 'reference_no'])
      refetchAdvances()
      qc.invalidateQueries({ queryKey: ['sp-pending'] })
    },
    onError: (e) => message.error(
      (e as { message?: string })?.message ?? 'Failed to record advance',
    ),
  })

  const onSubmit = () => form.validateFields().then((v) => submit.mutate(v))

  return (
    <div>
      <PageHeader
        title="PO Advance Payment"
        subtitle="Record advance against a PO + vendor. Balance is auto-calculated at invoice booking."
      />

      <Row gutter={12}>
        <Col xs={24} lg={12}>
          <Card title="Record advance">
            <Form form={form} layout="vertical">
              <Form.Item
                label="Purchase Order *"
                name="po_id"
                rules={[{ required: true }]}>
                <Select
                  showSearch
                  options={(pos ?? []).map((p) => ({
                    label: `${p.po_no} — ${p.supplier_name ?? p.supplier_id?.slice(0, 8)}`,
                    value: p.id,
                  }))}
                  filterOption={(i, o) =>
                    (o?.label as string).toLowerCase().includes(i.toLowerCase())
                  }
                  onChange={(v) => setPoId(v)}
                />
              </Form.Item>

              {po && (
                <Descriptions size="small" bordered column={2}
                  style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="PO No">{po.po_no}</Descriptions.Item>
                  <Descriptions.Item label="Vendor">
                    {po.supplier_name ?? '—'} <Tag>AUTO FETCHED</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="PO Status">
                    <Tag>{po.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Advance already paid">
                    ₹ {totalAdvancePaid.toFixed(2)} <Tag>SYSTEM CALCULATED</Tag>
                  </Descriptions.Item>
                </Descriptions>
              )}

              <Form.Item
                label="Advance Amount (₹) *"
                name="amount"
                rules={[{ required: true, type: 'number', min: 0.01 }]}>
                <InputNumber min={0} step={500} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Advance Date *"
                name="advance_date"
                initialValue={dayjs()}
                rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Expected Settlement Date"
                name="expected_settlement_date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item label="Bank / Payment Mode" name="bank_mode">
                <Input placeholder="NEFT / RTGS / Cheque" />
              </Form.Item>

              <Form.Item label="Reference No" name="reference_no">
                <Input />
              </Form.Item>

              <Form.Item label="Purpose / Remarks" name="purpose">
                <Input.TextArea rows={2}
                  placeholder="e.g. 30% advance against PO" />
              </Form.Item>

              <Button type="primary" onClick={onSubmit} loading={submit.isPending}>
                Record advance
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="PO advance status" size="small">
            {!poId && <Text type="secondary">Pick a PO to view advance status.</Text>}
            {poId && (
              <>
                <Descriptions size="small" bordered column={2}
                  style={{ marginBottom: 12 }}>
                  <Descriptions.Item label="Advances on this PO">
                    {(advances ?? []).length}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total advance paid">
                    ₹ {totalAdvancePaid.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Remaining unsettled">
                    <Text strong>₹ {totalAdvanceRemaining.toFixed(2)}</Text>
                    <Tag style={{ marginLeft: 6 }}>SYSTEM CALCULATED</Tag>
                  </Descriptions.Item>
                </Descriptions>
                <Table
                  size="small"
                  rowKey="advance_id"
                  pagination={false}
                  dataSource={advances}
                  columns={[
                    {
                      title: 'Date', dataIndex: 'advance_date', width: 130,
                      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
                    },
                    {
                      title: 'Amount', dataIndex: 'amount', width: 110,
                      render: (v: number) => `₹ ${v.toFixed(2)}`,
                    },
                    {
                      title: 'Settled', dataIndex: 'settled_amount', width: 110,
                      render: (v: number) => `₹ ${v.toFixed(2)}`,
                    },
                    {
                      title: 'Remaining', dataIndex: 'remaining', width: 110,
                      render: (v: number) => (
                        <Text strong style={{ color: v > 0 ? '#cf1322' : '#52c41a' }}>
                          ₹ {v.toFixed(2)}
                        </Text>
                      ),
                    },
                    {
                      title: 'Status', dataIndex: 'status', width: 110,
                      render: (v: string) => (
                        <Tag color={v === 'SETTLED' ? 'green'
                          : v === 'PARTIAL' ? 'orange' : 'default'}>{v}</Tag>
                      ),
                    },
                  ]}
                />
                <Alert
                  style={{ marginTop: 12 }}
                  showIcon
                  type="info"
                  message="Advance is auto-adjusted when the vendor invoice is posted. You never type the balance."
                />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
